import { useState, useMemo } from 'react'
import { format, parseISO } from 'date-fns'
import { FileText, Loader2, Check } from 'lucide-react'
import type { Activity, Tour } from '@/types'
import { formatInTimezone, getTimezoneAbbr } from '@/lib/timezones'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { Drawer, DrawerContent } from '@/components/ui/drawer'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'

interface Props {
  tour: Tour
  day: string
  activities: Activity[]
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function DaySheetDialog({ tour, day, activities, open, onOpenChange }: Props) {
  const isDesktop = useMediaQuery('(min-width: 640px)')

  const body = (
    <DaySheetBody tour={tour} day={day} activities={activities} onClose={() => onOpenChange(false)} />
  )

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md p-0 overflow-x-hidden">
          <DialogHeader className="sr-only">
            <DialogTitle>Day sheet</DialogTitle>
            <DialogDescription>Select activities to include in the PDF.</DialogDescription>
          </DialogHeader>
          {body}
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[88dvh]">
        <div className="overflow-y-auto">{body}</div>
      </DrawerContent>
    </Drawer>
  )
}

function DaySheetBody({
  tour, day, activities, onClose,
}: {
  tour: Tour
  day: string
  activities: Activity[]
  onClose: () => void
}) {
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(activities.map(a => a.id))
  )
  const [generating, setGenerating] = useState(false)

  const allSelected = selected.size === activities.length
  const noneSelected = selected.size === 0

  const sortedActivities = useMemo(
    () => [...activities].sort((a, b) => a.start_time_utc.localeCompare(b.start_time_utc)),
    [activities]
  )

  function toggle(id: string) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(activities.map(a => a.id)))
  }

  async function handleGenerate() {
    if (noneSelected) return
    setGenerating(true)
    try {
      const { buildAndDownloadDaySheet } = await import('@/lib/daySheet')
      const chosen = sortedActivities.filter(a => selected.has(a.id))
      await buildAndDownloadDaySheet({ tour, day, activities: chosen })
      toast.success('Day sheet generated')
      onClose()
    } catch (err) {
      console.error(err)
      const { captureException } = await import('@/lib/sentry')
      captureException(err, { tour: tour.id, day, activityCount: selected.size })
      const msg = err instanceof Error ? err.message : String(err)
      toast.error(`Failed to generate day sheet: ${msg}`)
    } finally {
      setGenerating(false)
    }
  }

  const dateLong = format(parseISO(day), 'EEEE, MMMM d')

  return (
    <div className="flex flex-col max-h-[88dvh]">
      {/* Hero — padding mirrors the ActivityDetailDrawer spec so both
          drawers feel consistent when opened from the schedule. */}
      <div className="px-5 pt-3 pb-5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--subtle-foreground)]">
          Day sheet
        </p>
        <h2 className="text-[24px] font-semibold leading-[1.1] tracking-[-0.02em] mt-1.5 text-foreground">
          {dateLong}
        </h2>
        <p className="text-[13px] text-muted-foreground mt-1.5 leading-relaxed">
          <span className="text-foreground font-medium">{tour.nombre}</span>
          <span className="mx-1.5 text-[color:var(--subtle-foreground)]">·</span>
          Select what goes on the printed sheet.
        </p>
      </div>

      {/* Select-all strip */}
      <div className="flex items-center justify-between px-5 py-2.5 border-y border-border bg-card-2/40">
        <span className="text-xs text-muted-foreground tabular-nums">
          <span className="text-foreground font-medium">{selected.size}</span> of {activities.length} selected
        </span>
        <button
          type="button"
          onClick={toggleAll}
          className="text-xs text-primary font-semibold hover:underline underline-offset-2"
        >
          {allSelected ? 'Deselect all' : 'Select all'}
        </button>
      </div>

      {/* Checklist */}
      <div className="flex-1 overflow-y-auto">
        {sortedActivities.length === 0 ? (
          <p className="text-sm text-muted-foreground italic px-5 py-8 text-center">
            No activities on this day to include.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {sortedActivities.map(a => {
              const isSel = selected.has(a.id)
              const start = formatInTimezone(a.start_time_utc, a.venue_timezone)
              const tzAbbr = getTimezoneAbbr(a.venue_timezone)
              const isPublic = a.visibility_level === 'public'
              return (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => toggle(a.id)}
                    className={cn(
                      'w-full flex items-start gap-3 px-5 py-3.5 text-left transition-colors',
                      isSel ? 'bg-card' : 'bg-transparent hover:bg-card-2/60'
                    )}
                  >
                    {/* Backlot-styled checkbox — primary-fill when checked,
                        bordered card when unchecked. */}
                    <span
                      className={cn(
                        'mt-[3px] w-[18px] h-[18px] rounded-[5px] border flex items-center justify-center shrink-0 transition-all dur-fast',
                        isSel
                          ? 'bg-primary border-primary text-primary-foreground'
                          : 'bg-transparent border-border-strong'
                      )}
                      aria-hidden="true"
                    >
                      {isSel && <Check className="w-[11px] h-[11px]" strokeWidth={3.5} />}
                    </span>

                    <div className="flex-1 min-w-0">
                      {/* Time row — mono + tz, aligned to the same baseline */}
                      <div className="flex items-center gap-2 text-xs">
                        <span className="mono font-medium text-foreground tabular-nums">{start}</span>
                        <span className="mono text-[10px] text-[color:var(--subtle-foreground)] tracking-wider">
                          {tzAbbr}
                        </span>
                        <span className={cn('vis-chip', isPublic ? 'vis-chip-public' : 'vis-chip-assigned')}>
                          {a.visibility_level}
                        </span>
                      </div>
                      <p className="text-[14px] font-medium text-foreground leading-snug mt-1 truncate">
                        {a.titulo}
                      </p>
                      {a.venue_name && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{a.venue_name}</p>
                      )}
                    </div>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {/* Footer — Backlot pattern buttons, same height/radius as elsewhere.
          pb-safe keeps the buttons clear of the iOS home indicator; extra
          pb on desktop gives the accent button's lift shadow room to breathe. */}
      <div className="px-5 pt-3 pb-5 sm:pb-6 border-t border-border flex gap-2 pb-safe">
        <button
          type="button"
          onClick={onClose}
          className="login-btn compact flex-1"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={generating || noneSelected}
          className="login-btn compact accent flex-[1.4] gap-2"
        >
          {generating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <FileText className="w-4 h-4" />
          )}
          {generating ? 'Generating…' : 'Generate PDF'}
        </button>
      </div>
    </div>
  )
}
